// The dice bag's sections (qa-runs#368, slice B): Attacks, Saves, Skill checks and Ability checks for the selected
// token, beside the bag's Dice. Each section folds, its open state kept with this person's layout (`PanelPrefs.dice`).
// The numbers are the token's source's: a linked character's sheet (read with this person's bearer), or, for the DM, a
// linked creature's stat block; a player is never sent a creature's. Attacks are the attack form's own choices
// (`useOwnAttacks`), so a pick opens that form already filled in. Every other button is one ordinary throw of the
// system's save dice plus the number, made by the bag as any of its throws (privacy, Roll for, the band, the token).
import { useSheetPulse } from '../../sheetPulse';
import { AdvantageControl } from './AdvantageControl';
import type { AdvantageWord, RollRequest } from '@dragons-table/vtt-shared';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { DiceSection } from '@dragons-table/vtt-shared';
import { fetchChecks, type ChecksAnswer, type CheckRow } from '../../entries';
import { useDiceSectionOpen } from '../panelPrefs';
import { useTableState } from '../../table/TableState';
import { useShell } from '../context';
import { useOwnAttacks } from './AttackParts';
import { BTN, SECTION_HEAD } from '../ui';
// Each entry drags onto a hot bar box (qa-runs#369 slice B); a click still rolls it.
import { HOT_LIMITS } from '@dragons-table/vtt-shared';
import { hotDragProps } from '../../board/hotDrag';

const signed = (n: number) => `${n >= 0 ? '+' : ''}${n}`;
const chip = `${BTN} flex items-center justify-between gap-1 text-left`;

/**
 * A one-off extra modifier typed before a throw (qa-runs#368): "+2 circumstance", "-1", "3". Null when nothing is
 * typed; `bad` when it does not start with a whole number.
 */
export function parseExtra(text: string): { value: number; note: string } | null | 'bad' {
  const t = text.trim();
  if (!t) return null;
  const m = t.replace('−', '-').match(/^([+-]?)\s*(\d{1,4})\b\s*(.*)$/);
  if (!m) return 'bad';
  const value = (m[1] === '-' ? -1 : 1) * Number(m[2]);
  const word = m[3]!.trim().slice(0, 30);
  return { value, note: `${signed(value)}${word ? ` ${word}` : ''}` };
}

/** One folding section of the bag, remembered open or closed for this person. */
export function CheckSection({ id, title, children, extra }: { id: DiceSection; title: string; children: ReactNode; extra?: ReactNode }) {
  const [open, setOpen] = useDiceSectionOpen(id);
  return (
    <section aria-label={title} data-testid={`dice-section-${id}`} data-open={open ? 'yes' : 'no'} className="border-t border-stone-800 pt-1">
      <div className="flex items-center gap-1">
        <button type="button" aria-expanded={open} className={`${SECTION_HEAD} flex flex-1 items-center gap-1 text-left`} onClick={() => setOpen(!open)}>
          <span aria-hidden="true">{open ? '▾' : '▸'}</span>{title}
        </button>
        {open && extra}
      </div>
      {open && <div className="mt-1 flex flex-col gap-1">{children}</div>}
    </section>
  );
}

/** Where a row's number came from, said on its button. */
const sourceTitle = (row: { total: number; lines?: CheckRow['lines'] }, from: 'sheet' | 'stat block' | null): string => {
  const where = from === 'sheet' ? 'the sheet' : from === 'stat block' ? 'the stat block' : 'its source';
  return `${signed(row.total)} from ${where}${row.lines?.length ? `: ${row.lines.map((l) => `${l.label} ${l.value}`).join(', ')}` : ''}`;
};

/**
 * The selected token's sections. `onRoll` throws the save dice with a label and a number; `onAttack` opens the attack
 * form at one of the token's attacks (by its key). Third System's tests stay its own: a system whose saves throw no
 * plain dice is sent none of these.
 */
export function TokenChecks({ tokenId, name, isDm, onRoll, onAttack }: {
  tokenId: string; name: string; isDm: boolean;
  onRoll: (label: string, dice: { count: number; sides: number }, total: number, advantage?: AdvantageWord, check?: RollRequest['check']) => void;
  onAttack: (key: string) => void;
}) {
  const { systemRules, scene, store } = useTableState();
  const { campaignId } = useShell();
  const { own, asking } = useOwnAttacks(tokenId, isDm);
  const [checks, setChecks] = useState<ChecksAnswer | 'asking'>('asking');
  const [find, setFind] = useState('');
  // Read again when its link, its numbers or its sheet change (qa-runs 1257, 1258).
  const held = store.state.objects[tokenId] as { character?: string | null; values?: { sheet?: unknown } } | undefined;
  const sheetRead = `${useSheetPulse(tokenId)} ${held?.character ?? ''} ${JSON.stringify(held?.values?.sheet ?? null)}`;
  useEffect(() => {
    let live = true;
    setChecks('asking');
    if (systemRules.test) { setChecks(null); return () => { live = false; }; }
    void fetchChecks(campaignId, tokenId, scene?.id ?? null).then((c) => { if (live) setChecks(c); });
    return () => { live = false; };
  }, [campaignId, tokenId, scene?.id, systemRules.test, sheetRead]);
  const got = checks === 'asking' ? null : checks;
  const skills = useMemo(() => {
    const q = find.trim().toLowerCase();
    return (got?.skills ?? []).filter((s) => !q || s.name.toLowerCase().includes(q));
  }, [got, find]);
  const dice = got?.dice ?? null;
  const [word, setWord] = useState<AdvantageWord | null>(null);
  const roll = (label: string, total: number, on: NonNullable<RollRequest['check']>['on'], which?: string) => { if (dice) onRoll(`${name}: ${label}`, dice, total, word ?? undefined, { on, token: tokenId, ...(which ? { which } : {}) }); };
  return (
    <>
      {dice?.advantage && <AdvantageControl id="checks" label="Advantage on these rolls" value={word} onChange={(a) => setWord(a === 'normal' ? null : a)} />}
      {systemRules.attack && (
        <CheckSection id="attacks" title="Attacks">
          {asking && <p className="text-xs text-stone-500">Reading its attacks…</p>}
          {!asking && own.length === 0 && <p className="text-xs text-stone-500" data-testid="dice-attacks-none">No attacks to read: use Attack… below and type it.</p>}
          {own.map((c) => (
            <button key={c.key} type="button" className={chip} data-testid={`dice-attack-${c.key}`} {...hotDragProps({ token: tokenId, ref: `attack:${c.key}`, label: c.label })} title={`Opens the attack form with ${c.label}, filled in from the ${c.from ?? 'source'}.`} onClick={() => onAttack(c.key)}>
              <span>{c.label}</span>
            </button>
          ))}
        </CheckSection>
      )}
      {checks === 'asking' && !systemRules.test && <p className="text-xs text-stone-500" data-testid="dice-checks-asking">Reading its saves and skills…</p>}
      {got?.notice && <p className="text-xs text-amber-300" data-testid="dice-checks-notice">{got.notice}</p>}
      {dice && got && got.saves.length > 0 && (
        <CheckSection id="saves" title="Saves">
          <div className="grid grid-cols-3 gap-1">
            {got.saves.map((s) => (
              <button key={s.key} type="button" className={chip} aria-label={`Roll ${s.label} save ${signed(s.total)}`} {...hotDragProps({ token: tokenId, ref: `save:${s.key}`, label: `${s.label} save` })} title={sourceTitle(s, got.from)} onClick={() => roll(`${s.label} save`, s.total, 'save', s.key)}>
                <span>{s.label}</span><b>{signed(s.total)}</b>
              </button>
            ))}
          </div>
        </CheckSection>
      )}
      {dice && got && got.skills.length > 0 && (
        <CheckSection id="skills" title="Skill checks">
          <input aria-label="Find a skill" placeholder="Find a skill" className="rounded bg-stone-800 px-2 py-1 text-xs" value={find} onChange={(e) => setFind(e.target.value)} />
          <ul className="flex max-h-48 flex-col gap-0.5 overflow-y-auto" data-testid="dice-skills">
            {skills.map((s) => (
              <li key={s.key ?? s.name}>
                <button type="button" {...hotDragProps({ token: tokenId, ref: `skill:${s.key ?? s.name}`.slice(0, HOT_LIMITS.ref), label: s.name })} className={`${chip} w-full ${s.lacks ? 'text-stone-400' : ''}`} aria-label={`Roll ${s.name} ${signed(s.total)}${s.lacks ? ' (trained only, no ranks)' : s.trainedOnly ? ' (trained only)' : ''}`}
                  title={s.lacks ? `Trained only, and this character has no ranks in it. ${sourceTitle(s, got.from)}` : sourceTitle(s, got.from)} onClick={() => roll(`${s.name} check`, s.total, 'skill', s.key ?? undefined)}>
                  <span>{s.name}{s.trainedOnly && <span className={`ml-1 text-[0.625rem] ${s.lacks ? 'text-rose-400' : 'text-stone-500'}`} data-testid={s.lacks ? 'skill-lacked' : 'skill-trained'}>{s.lacks ? 'trained only · no ranks' : 'trained only'}</span>}</span>
                  <b>{signed(s.total)}</b>
                </button>
              </li>
            ))}
            {skills.length === 0 && <li className="text-xs text-stone-500">No skill matches.</li>}
          </ul>
        </CheckSection>
      )}
      {dice && got && got.abilities.length > 0 && (
        <CheckSection id="abilities" title="Ability checks">
          <div className="grid grid-cols-3 gap-1">
            {got.abilities.map((a) => (
              <button key={a.key} type="button" className={chip} aria-label={`Roll ${a.label} check ${signed(a.total)}`} {...hotDragProps({ token: tokenId, ref: `ability:${a.key}`, label: `${a.label} check` })} title={sourceTitle(a, got.from)} onClick={() => roll(`${a.label} check`, a.total, 'ability_check', a.key)}>
                <span className="truncate">{a.label}</span><b>{signed(a.total)}</b>
              </button>
            ))}
          </div>
        </CheckSection>
      )}
    </>
  );
}
